import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Modal,
    Button,
    TextField,
    Typography,
    Box,
    Grid,
    Paper,
    Divider,
    Alert,
    Tabs,
    Tab,
    IconButton,
    Tooltip,
    Card,
    CardContent,
    Slider,
    CircularProgress,
    Chip,
} from '@mui/material';
import DocumentPreview from './DocumentPreview';
import DocumentStylePicker from './DocumentStylePicker';
import DesignVersionsPanel from './DesignVersionsPanel';
import HeaderDetailsEditor from './HeaderDetailsEditor';
import AutocompleteSelectFilter from '../AutocompleteSelectFilter/AutocompleteSelectFilter';
import {
    Visibility,
    Save,
    Refresh,
    Close as CloseIcon,
    Settings as SettingsIcon,
    Create as CreateIcon,
    Image as ImageIcon,
    Description as DescriptionIcon,
    ViewColumn as ViewColumnIcon,
    Upload as UploadIcon,
    TextIncrease as TextIncreaseIcon,
    DesignServices as DesignServicesIcon,
    Add as AddIcon,
    Draw as DrawIcon,
    OpenInNew as OpenInNewIcon,
    BusinessCenter as BusinessCenterIcon,
    Style as StyleIcon,
    Wallpaper as WallpaperIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import SignaturePad from 'react-signature-canvas';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import html2canvas from 'html2canvas';
import { GetDocumentSettings, UpdateDocumentSettings } from '../../../Services/documentSettings.service';
import {
    GetDesignVersions,
    CreateDesignVersion,
    UpdateDesignVersion,
    DeleteDesignVersion,
} from '../../../Services/designVersions.service';
import DocumentService from '../../../Services/documents.service';
import ClientService from '../../../Services/client.service.js';
import ClientItemsService from '../../../Services/clientItems.service';
import { toast } from 'react-toastify';
import { styled } from '@mui/material/styles';
import DraggableModal from '../../DraggableModal/DraggableModal.js';
import NoDocumentsPdf from '../../../assests/pdf/NoDocument.pdf';
import { DEFAULT_DOCUMENT_SETTINGS } from '../../../Utils/DocumentVersion/documentSettingsDefaults';
import { applyStyleVersionToColumnSettings } from '../../../Utils/DocumentVersion/documentStyleVersions';
import {
    BASE_DESIGN_ID,
    designToSettings,
    settingsToDesignPayload,
    buildDocumentSettingsPayload,
    buildHeaderDetailsFromClient,
    getDefaultHeaderLabels,
    getStyleDisplayName,
} from '../../../Utils/DocumentVersion/documentDesignVersions';
import ClientItemCreateModal from '../../ClientItems/SubComponents/ClientItemCreateModal.js';
import Swal from 'sweetalert2';
import styles from './DocumentSettingsDialog.module.scss';

// Backend DocumentTypes enum mapping for preview only
const DocumentTypesEnum = [
    { value: 'TaxInvoice', label: "חשבונית מס" },
    { value: 'ReceiptTax', label: "חשבונית מס קבלה" },
    { value: 'CreditNote', label: "חשבונית מס זיכוי" },
    { value: 'Receipt', label: "קבלה" },
    { value: 'Quotation', label: "הצעת מחיר" },
    { value: 'Order', label: "הזמנה" },
    { value: 'Transaction', label: "חשבון עסקה" },
    { value: 'PostponedReceipt', label: "קבלת דחויים" },
    { value: 'Deposit', label: "משיכה/הפקדה" },
];



const StyledButton = styled(Button)(({ theme, variant = 'contained', direction, size = 'medium' }) => ({
    borderRadius: '10px',
    padding: size === 'small' ? '6px 12px' :
        size === 'medium' ? '6px 14px' :
            '8px 16px',
    fontWeight: 600,
    textTransform: 'none',
    fontFamily: 'var(--xtram-font)',
    fontSize: size === 'small' ? '12px' :
        size === 'medium' ? '13px' :
            '14px',
    minHeight: size === 'small' ? '28px' :
        size === 'medium' ? '36px' :
            '40px',
    transition: 'background 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
    direction: direction === 'rtl' ? 'rtl' : 'ltr',
    boxShadow: 'none',
    ...(variant === 'contained' ? {
        background: 'var(--xtram-orange)',
        color: 'var(--xtram-white)',
        '&:hover': {
            background: 'color-mix(in srgb, var(--xtram-orange) 88%, #000)',
            boxShadow: '0 4px 12px color-mix(in srgb, var(--xtram-orange) 35%, transparent)',
        },
    } : {
        border: '1px solid var(--xtram-blue)',
        color: 'var(--xtram-blue)',
        background: 'transparent',
        '&:hover': {
            background: 'rgba(48, 79, 255, 0.08)',
            borderColor: 'var(--xtram-blue)',
        },
    }),
}));

// MUI's startIcon margins are LTR-only; logical margins keep the icon beside the label in RTL too.
const cardActionButtonSx = {
    width: '100%',
    minWidth: 0,
    whiteSpace: 'nowrap',
    '& .MuiButton-startIcon': { margin: 0, marginInlineEnd: '6px' },
};

const StyledCard = styled(Card)(({ theme, direction }) => ({
    background: '#ffffff',
    border: '1px solid #e5e7f6',
    borderRadius: '12px',
    overflow: 'hidden',
    transition: 'all 0.2s ease',
    direction: direction === 'rtl' ? 'rtl' : 'ltr',
    boxShadow: 'none',
    '&:hover': {
        borderColor: 'var(--xtram-blue)',
        boxShadow: '0 0 0 2px rgba(48, 79, 255, 0.08)',
    },
}));

const StyledSlider = styled(Slider)(({ theme, direction }) => ({
    direction: direction === 'rtl' ? 'rtl' : 'ltr',
    padding: '6px 0',
    height: 6,
    '& .MuiSlider-rail': {
        height: 4,
        borderRadius: 2,
        backgroundColor: '#e5e7f6',
        opacity: 1,
    },
    '& .MuiSlider-track': {
        background: 'var(--xtram-blue)',
        height: 4,
        borderRadius: 2,
        border: 'none',
    },
    '& .MuiSlider-thumb': {
        backgroundColor: 'var(--xtram-blue)',
        width: 14,
        height: 14,
        border: '2px solid #ffffff',
        boxShadow: '0 1px 4px rgba(48, 79, 255, 0.3)',
        '&:hover, &.Mui-focusVisible': {
            boxShadow: '0 0 0 6px rgba(48, 79, 255, 0.16)',
        },
    },
    '& .MuiSlider-mark': {
        width: 1,
        height: 4,
        borderRadius: '50%',
        backgroundColor: '#4285f4',
        transform: 'translate(-50%, -50%)',
        top: '50%',
        opacity: 1,
    },
    '& .MuiSlider-markActive': {
        backgroundColor: 'var(--xtram-blue)',
        opacity: 1,
    },
    '& .MuiSlider-markLabel': {
        display: 'none',
    },
}));

const DocumentSettingsDialog = ({
    open,
    onClose,
    onSave,
    token,
    accountantId,
    caseId,
    direction,
    client
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');
    const [activeTab, setActiveTab] = useState(0);
    const [settingsTab, setSettingsTab] = useState('style');

    const settingsTabs = [
        { id: 'style', icon: StyleIcon, label: t('DocumentStyleVersion') || 'גרסת עיצוב מסמך' },
        { id: 'signature', icon: CreateIcon, label: t('SignatureAndProfilePicture') },
        { id: 'columns', icon: ViewColumnIcon, label: t('ColumnSettings') },
        { id: 'description', icon: DescriptionIcon, label: t('DescriptionAndFooter') },
    ];

    // Document settings state - simplified to one settings object per case
    const [settings, setSettings] = useState({
        customDescription: '',
        customFooter: '',
        headerRightText: '',
        headerLeftText: '',
        itemsDisplayMode: '',
        columnSettings: {
            ...DEFAULT_DOCUMENT_SETTINGS.columnSettings,
        }
    });
    // Base design (DocumentSettings) + saved version rows; `settings` holds the active target's editable state
    const [baseDesign, setBaseDesign] = useState(null);
    const [designVersions, setDesignVersions] = useState([]);
    const [activeTargetId, setActiveTargetId] = useState(BASE_DESIGN_ID);
    // Layout chosen in the "new version" chooser; null while the chooser is closed
    const [newVersionStyleId, setNewVersionStyleId] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    // A version with an empty custom footer uses the base design's footer
    const inheritedCustomFooter = activeTargetId !== BASE_DESIGN_ID ? baseDesign?.customFooter || '' : '';
    const effectiveSettings = !settings.customFooter && inheritedCustomFooter
        ? { ...settings, customFooter: inheritedCustomFooter }
        : settings;
    // Programmatic settings loads (open / switch target) must not mark the target dirty
    const skipDirtyRef = useRef(true);
    const [newPageBackgroundFile, setNewPageBackgroundFile] = useState(null);
    const [pageBackgroundModified, setPageBackgroundModified] = useState(false);
    const bgDragRef = useRef(null);
    const bgDraggingRef = useRef(false);
    const bgDragLastRef = useRef({ x: 0, y: 0 });

    const [selectedDocumentType, setSelectedDocumentType] = useState('TaxInvoice'); // Default to TaxInvoice for preview only
    const iframeRef = useRef(null);

    // Signature and Profile Picture states
    const [signaturePreview, setSignaturePreview] = useState('');
    const [profilePreview, setProfilePreview] = useState('');
    const [newSignatureFile, setNewSignatureFile] = useState(null);
    const [newProfileFile, setNewProfileFile] = useState(null);
    const [signatureModified, setSignatureModified] = useState(false);
    const [profileModified, setProfileModified] = useState(false);
    const [addSignaturePad, setAddSignaturePad] = useState(false);
    const [textSignatureOption, setTextSignatureOption] = useState(false);
    const [textSignature, setTextSignature] = useState('');
    const [selectedFont, setSelectedFont] = useState('Arial');

    // HTML preview state
    const [previewMode, setPreviewMode] = useState('html'); // 'html' or 'pdf'
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfPreview, setPdfPreview] = useState(null);

    // DraggableModal state variables
    const [openedIframeUrl, setOpenedIframeUrl] = useState('');
    const [bigIframeModalOpen, setBigIframeModalOpen] = useState(false);

    // Default item for documents (client items + current default)
    const [clientItems, setClientItems] = useState([]);
    const [defaultItem, setDefaultItem] = useState(null);
    const [selectedItemMakat, setSelectedItemMakat] = useState('');
    const [defaultItemLoading, setDefaultItemLoading] = useState(false);
    const [createItemModalOpen, setCreateItemModalOpen] = useState(false);
    const [createItemLoading, setCreateItemLoading] = useState(false);

    const sigPad = useRef();

    
    const fonts = [
        'Arial', 'Montserrat', 'Courier New', 'Comic Sans MS', 'Georgia', 'Times New Roman', 'Verdana',
        'Trebuchet MS', 'Tahoma', 'Helvetica', 'Impact', 'PT Sans Narrow'
    ];
    const documentFontOptions = ['Arial', ...fonts.filter((font) => font !== 'Arial')];
    const documentFontSelectOptions = useMemo(
        () => documentFontOptions.map((font) => ({ value: font, label: font })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );
    const signatureFontOptions = useMemo(
        () => fonts.map((font) => ({ value: font, label: font })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );
    const itemsDisplayModeOptions = useMemo(() => [
        { value: 'normal', label: t('NormalDisplay') },
        { value: 'descriptionOnly', label: t('DescriptionOnly') },
    ], [t]);
    const logoPositionOptions = useMemo(() => [
        { value: 'center', label: t('Center') },
        { value: 'left', label: t('Left') },
    ], [t]);
    const dateAlignOptions = useMemo(() => [
        { value: 'right', label: t('AlignRight') || 'יישור לימין' },
        { value: 'left', label: t('AlignLeft') || 'יישור לשמאל' },
    ], [t]);
    const totalsLayoutOptions = useMemo(() => [
        { value: 'horizontal', label: t('Horizontal') },
        { value: 'vertical', label: t('Vertical') },
    ], [t]);
    const profilePictureShapeOptions = useMemo(() => [
        { value: 'NoChooseShape', label: t('NoChooseShape') },
        { value: 'circle', label: t('Circle') },
        { value: 'rectangle', label: t('Rectangle') },
        { value: 'hexagon', label: t('Hexagon') },
        { value: 'diamond', label: t('Diamond') },
        { value: 'roundedRectangle', label: t('RoundedRectangle') },
        { value: 'oval', label: t('Oval') },
        { value: 'triangle', label: t('Triangle') },
    ], [t]);
    const defaultItemSelectOptions = useMemo(() => [
        { value: '', label: t('NoneText') || 'ללא' },
        { value: '__add_client_item__', label: `+ ${t('AddClientItem')}` },
        ...(clientItems || []).map((item) => ({
            value: item.makat || '',
            label: item.makat
                ? `${item.makat} – ${item.name}${defaultItem?.makat === item.makat ? ` (${t('Default') || 'ברירת מחדל'})` : ''}`
                : item.name,
        })),
    ], [t, clientItems, defaultItem]);

    const pickOption = (options, value) =>
        options.find((o) => o.value === value) || options[0] || null;

    // DraggableModal functions
    const openPdfInNewTab = (pdfUrl) => {
        if (pdfUrl) {
            setOpenedIframeUrl(pdfUrl ? pdfUrl : NoDocumentsPdf);
            setBigIframeModalOpen(true);
        }
    };

    const handleCloseIframeModal = () => {
        setBigIframeModalOpen(false);
        setOpenedIframeUrl('');
    };
    // Helper function to check if document should show table (based on backend logic)
    const shouldShowTable = (documentType) => {
        // Based on backend logic: IsInvoiceReceipt determines if table is shown
        // Documents that show tables: TaxInvoice, ReceiptTax, CreditNote, Receipt, PostponedReceipt
        return ['TaxInvoice', 'ReceiptTax', 'CreditNote', 'Receipt', 'PostponedReceipt'].includes(documentType);
    };


    // Helper function to check if document is deferred receipt
    const isDeferredReceipt = (documentType) => {
        return documentType === 'PostponedReceipt';
    };

    // Helper function to get the correct document type for backend
    const getBackendDocumentType = (documentType) => {
        // Backend only supports TaxInvoice and Receipt for document settings
        // PostponedReceipt should be treated as Receipt for settings but with DeferredReceipt flag
        if (documentType === 'PostponedReceipt') {
            return 'Receipt';
        }
        // For preview generation, we need to determine if it should be treated as TaxInvoice or Receipt
        // based on whether it shows a table (IsInvoiceReceipt)
        return shouldShowTable(documentType) ? 'TaxInvoice' : 'Receipt';
    };

    useEffect(() => {
        if (open) {
            loadDocumentSettings();
            loadCurrentSignatureAndProfile();
            loadClientItemsAndDefault();
        }
    }, [open, caseId, token]);

    // Any edit to the active target's settings marks it as unsaved
    useEffect(() => {
        if (skipDirtyRef.current) {
            skipDirtyRef.current = false;
            return;
        }
        setIsDirty(true);
    }, [settings]);

    // Settings loaded before the client arrived: seed the header once from the business record
    useEffect(() => {
        if (!open || !client || settings.columnSettings?.headerDetails) return;
        skipDirtyRef.current = true;
        setSettings((prev) => ({
            ...prev,
            columnSettings: {
                ...prev.columnSettings,
                headerDetails: buildHeaderDetailsFromClient(client, getDefaultHeaderLabels(t, direction)),
            },
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, client, settings.columnSettings?.headerDetails]);

    const loadCurrentSignatureAndProfile = async () => {
        if (!client) {
            return;
        }

        try {
            // Reset modified flags
            setSignatureModified(false);
            setProfileModified(false);
            
            // Load current signature if exists
            // Backend now returns pre-signed URLs directly
            if (client.signature) {
                setSignaturePreview(client.signature);
            }

            // Load current profile picture if exists
            // Backend now returns pre-signed URLs directly
            if (client.profilePicture) {
                setProfilePreview(client.profilePicture);
            }
        } catch (error) {
            console.error('Error loading current signature and profile:', error);
        }
    };

    const loadClientItemsAndDefault = async () => {
        if (!caseId || !token) return;
        try {
            const [itemsRes, defaultRes] = await Promise.all([
                ClientItemsService.GetClientItemsByCaseId(caseId, token),
                ClientItemsService.GetDefaultClientItem(caseId, token),
            ]);
            if (itemsRes.ok) {
                const data = await itemsRes.json();
                setClientItems(Array.isArray(data) ? data : []);
            }
            if (defaultRes.ok) {
                const text = await defaultRes.text();
                const def = (text && text.trim()) ? JSON.parse(text) : null;
                setDefaultItem(def);
                setSelectedItemMakat(def?.makat ?? '');
            } else {
                setDefaultItem(null);
                setSelectedItemMakat('');
            }
        } catch (err) {
            console.error('Error loading client items / default item:', err);
            setClientItems([]);
            setDefaultItem(null);
            setSelectedItemMakat('');
        }
    };

    const handleSetDefaultItem = async () => {
        if (!caseId || !token) {
            toast.warning(t('SelectItemToSetAsDefault') || 'בחר פריט להגדרה כברירת מחדל');
            return;
        }
        setDefaultItemLoading(true);
        try {
            const makatToSet = (selectedItemMakat || '').trim();
            const res = await ClientItemsService.SetDefaultClientItem(caseId, makatToSet, token);
            if (res.ok) {
                toast.success(makatToSet ? (t('DefaultItemSetSuccessfully') || 'פריט ברירת המחדל עודכן') : (t('DefaultItemClearedSuccessfully') || 'בוטל פריט ברירת המחדל'));
                await loadClientItemsAndDefault();
            } else {
                const errText = await res.text();
                toast.error(t('ErrorSettingDefaultItem') || 'שגיאה בעדכון פריט ברירת מחדל');
            }
        } catch (err) {
            console.error(err);
            toast.error(t('ErrorSettingDefaultItem') || 'שגיאה בעדכון פריט ברירת מחדל');
        }
        setDefaultItemLoading(false);
    };

    const handleSelectDefaultItemChange = (e) => {
        const value = e.target.value;
        if (value === '__add_client_item__') {
            setCreateItemModalOpen(true);
            return;
        }
        setSelectedItemMakat(value);
    };

    const handleSaveNewClientItem = async (itemsToAdd) => {
        if (!caseId || !token || !itemsToAdd?.length) return;
        setCreateItemLoading(true);
        try {
            const itemsWithImageUrls = await Promise.all(
                itemsToAdd.map(async (item) => {
                    let imageUrl = null;
                    if (item.imageFile) {
                        try {
                            const uploadResp = await ClientItemsService.UploadClientItemImage(
                                item.imageFile,
                                caseId,
                                item.makat,
                                token
                            );
                            if (uploadResp.ok) {
                                const uploadData = await uploadResp.json();
                                imageUrl = uploadData.imageUrl;
                            } else {
                                toast.warning(t('FailedToUploadImageForItem') + ` ${item.makat}`);
                            }
                        } catch (err) {
                            console.error('Image upload error:', err);
                        }
                    }
                    return {
                        CaseId: caseId,
                        Makat: item.makat,
                        Name: item.name,
                        Price: parseFloat(item.price) || 0,
                        IncludeVat: item.includeVat === true,
                        ImageUrl: imageUrl,
                    };
                })
            );
            const resp = await ClientItemsService.AddClientItems(itemsWithImageUrls, token);
            if (!resp.ok) {
                const errText = await resp.text();
                toast.error(t('FailedToAddClientItem') + `: ${errText}`);
            } else {
                toast.success(t('ClientItemAddedSuccessfully'));
                setCreateItemModalOpen(false);
                await loadClientItemsAndDefault();
                if (itemsToAdd.length > 0 && itemsToAdd[0].makat) {
                    setSelectedItemMakat(itemsToAdd[0].makat);
                }
            }
        } catch (err) {
            toast.error(t('ErrorAddingClientItem'));
            console.error(err);
        }
        setCreateItemLoading(false);
    };

    const loadDocumentSettings = async () => {
        try {
            setLoading(true);
            setError('');

            // Sequential: loading the settings moves any legacy versions into rows first
            const result = await GetDocumentSettings({ caseId }, token);
            const rows = await GetDesignVersions(caseId, token).catch((err) => {
                console.error('Error loading design versions:', err);
                return [];
            });

            const fallbackHeader = client
                ? buildHeaderDetailsFromClient(client, getDefaultHeaderLabels(t, direction))
                : null;
            const base = designToSettings(result || {}, fallbackHeader);
            const versions = Array.isArray(rows) ? rows : [];
            let rememberedId = null;
            try {
                rememberedId = localStorage.getItem(designTargetStorageKey) || null;
            } catch { /* storage unavailable */ }

            setBaseDesign(base);
            setDesignVersions(versions);
            loadTarget(versions.some((v) => v.id === rememberedId) ? rememberedId : BASE_DESIGN_ID, versions, base);
        } catch (err) {
            console.error('Error loading document settings:', err);
            setError(t('CheckDocumentSettings'));
        } finally {
            setLoading(false);
        }
    };
    // Simple function to generate PDF preview when needed (for save button)
    const generatePdfPreview = async () => {
        setPdfLoading(true);
        try {
            // This will be called only when user wants to see PDF preview or save
            // For now, we'll keep the existing PDF generation logic for the save functionality
            // Use default item for preview row when set, else sample item
            const previewItem = defaultItem && (defaultItem.makat || defaultItem.Makat)
                ? {
                    makat: defaultItem.makat ?? defaultItem.Makat ?? '001',
                    name: defaultItem.name ?? defaultItem.Name ?? 'מוצר/שירות לדוגמה',
                    quantity: '1',
                    price: String(parseFloat(defaultItem.price ?? defaultItem.Price ?? 0) || 1000),
                    discount: '0',
                    isNew: true
                }
                : {
                    makat: '001',
                    name: 'Sample Product/Service',
                    quantity: '1',
                    price: '1000',
                    discount: '0',
                    isNew: true
                };

            const documentData = {
                CaseId: caseId,
                AccountantId: accountantId,
                Name: client?.name || 'Sample Company',
                // Description: settings?.customDescription || (t('SampleDocumentForPreview') || 'מסמך דוגמא לתצוגה'),
                Reference: client?.clientCaseId ? `דוגמא-${client.clientCaseId}` : 'דוגמא-001',
                Date: new Date().toISOString(),
                DocumentDate: new Date().toISOString(),
                ClientName: client?.clientName || 'Sample Client',
                ClientCaseId: client?.clientCaseId || '123456789',
                PhoneNumber: client?.phoneNumber || '050-1234567',
                SecondaryPhoneNumber: client?.secondaryPhoneNumber || '',
                ClientPhoneNumber: client?.clientPhoneNumber || '050-9876543',
                Items: [previewItem],
                IsInvoiceReceipt: shouldShowTable(selectedDocumentType) && !isDeferredReceipt(selectedDocumentType),
                DeferredReceipt: isDeferredReceipt(selectedDocumentType),
                Title: DocumentTypesEnum.find(doc => doc.value === selectedDocumentType)?.label || 'Sample Document',
                Email: client?.email || '',
                Website: client?.website || '',
                Location: client?.location || '',
                ClientLocation: client?.clientLocation || '',
                ClientEmail: client?.clientEmail || '',
                PriceIncludingVAT: false,
                ForceNoTax: false,
                WithholdingTax: 0,
                Signature: signaturePreview,
                GeneratedBy: 'Preview',
                ForeignCurrencyCode: null,
                AddToInventory: false,
                reportingMonthDate: new Date().toISOString(),
                checks: [],
                bankTransactions: [],
                cash: [],
                creditCards: [],
                CustomDescription: settings.customDescription,
                CustomFooter: effectiveSettings.customFooter,
                ColumnSettings: settings.columnSettings,
                DesignVersionId: activeTargetId || null,
                type: ['TaxInvoice', 'ReceiptTax', 'CreditNote', 'Receipt', 'PostponedReceipt'].includes(selectedDocumentType) ? 'PaymentAcceptances' : selectedDocumentType.toLowerCase()
            };

            let controller, endpoint, type;

            if (['TaxInvoice', 'ReceiptTax', 'CreditNote', 'Receipt', 'PostponedReceipt'].includes(selectedDocumentType)) {
                controller = 'PaymentAcceptances';
                endpoint = 'GeneratePaymentAcceptanceDocumentDraftPdf';
                type = 'PaymentAcceptances';
            } else {
                controller = 'TransactionDocuments';
                endpoint = 'GenerateTransactionDocumentDraftPdf';
                type = selectedDocumentType.toLowerCase();
            }

            const pdfBlob = await DocumentService.CreateDraftInvoice(
                controller,
                endpoint,
                documentData,
                type,
                DocumentTypesEnum.find(doc => doc.value === selectedDocumentType)?.label || 'Sample Document',
                selectedDocumentType,
                token
            );

            if (pdfBlob) {
                const url = URL.createObjectURL(pdfBlob);
                setPdfPreview(url);
            } else {
                throw new Error('No PDF blob received from server');
            }
        } catch (err) {
            console.error('Error generating PDF preview:', err);
            toast.error(`Error generating PDF preview: ${err.message}`);
            // Fallback to HTML preview if PDF generation fails
            setPreviewMode('html');
        } finally {
            setPdfLoading(false);
        }
    };





    const handleSignatureUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const objectUrl = URL.createObjectURL(file);
            setSignaturePreview(objectUrl);
            setNewSignatureFile(file);
            setSignatureModified(true);
            // Force HTML preview mode for live preview
            if (previewMode === 'pdf') {
                setPreviewMode('html');
            }
        }
    };

    const handleProfilePictureUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const objectUrl = URL.createObjectURL(file);
            setProfilePreview(objectUrl);
            setNewProfileFile(file);
            setProfileModified(true);
            // Force HTML preview mode for live preview
            if (previewMode === 'pdf') {
                setPreviewMode('html');
            }
        }
    };

    const handleSignatureChange = () => {
        if (sigPad.current) {
            const signatureDataUrl = sigPad.current.toDataURL();
            setSignaturePreview(signatureDataUrl);
            setNewSignatureFile(null);
            setSignatureModified(true);
            // Force HTML preview mode for live preview
            if (previewMode === 'pdf') {
                setPreviewMode('html');
            }
        }
    };

    const handleClearSignature = () => {
        if (sigPad.current) {
            sigPad.current.clear();
        }
        setSignaturePreview('');
        setNewSignatureFile(null);
        setTextSignature('');
        setSignatureModified(true);
        // Force HTML preview mode for live preview
        if (previewMode === 'pdf') {
            setPreviewMode('html');
        }
    };

    const handleRemoveSignature = async () => {
        if (!signaturePreview) return;
        const result = await Swal.fire({
            title: t('confirmDeleteTitle') || 'Confirm Remove',
            text: t('AreYouSureRemoveSignature') || 'Are you sure you want to remove the signature? This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('YesDelete') || 'Yes, Delete',
            cancelButtonText: t('Cancel') || 'Cancel',
            reverseButtons: true
        });
        if (!result.isConfirmed) return;
        try {
            const response = await ClientService.ClearSignature(token, caseId);
            if (response.ok) {
                setSignaturePreview('');
                setNewSignatureFile(null);
                setSignatureModified(false);
                toast.success(t('SignatureClearedSuccessfully') || 'Signature cleared successfully');
                // Update document settings entity and refresh client data (same as handleSave)
                await persistActiveTarget(settings);
                if (onSave) {
                    await onSave();
                }
            } else {
                toast.error(t('ErrorClearingSignature') || 'Error clearing signature');
            }
        } catch (err) {
            console.error(err);
            toast.error(t('ErrorClearingSignature') || 'Error clearing signature');
        }
    };

    const handleRemoveProfilePicture = async () => {
        if (!profilePreview) return;
        const result = await Swal.fire({
            title: t('confirmDeleteTitle') || 'Confirm Remove',
            text: t('AreYouSureRemoveProfilePicture') || 'Are you sure you want to remove the profile picture? This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('YesDelete') || 'Yes, Delete',
            cancelButtonText: t('Cancel') || 'Cancel',
            reverseButtons: true
        });
        if (!result.isConfirmed) return;
        try {
            const response = await ClientService.ClearProfilePhoto(token, caseId);
            if (response.ok) {
                setProfilePreview('');
                setNewProfileFile(null);
                setProfileModified(false);
                toast.success(t('ProfilePhotoClearedSuccessfully') || 'Profile photo cleared successfully');
                // Update document settings entity and refresh client data (same as handleSave)
                await persistActiveTarget(settings);
                if (onSave) {
                    await onSave();
                }
            } else {
                toast.error(t('ErrorClearingProfilePhoto') || 'Error clearing profile photo');
            }
        } catch (err) {
            console.error(err);
            toast.error(t('ErrorClearingProfilePhoto') || 'Error clearing profile photo');
        }
    };

    const generateTextSignatureImage = async () => {
        if (!textSignature.trim()) {
            toast.error(t('PleaseEnterTextForSignature') || 'Please enter text for signature');
            return;
        }

        const hiddenDiv = document.createElement('div');
        hiddenDiv.innerHTML = textSignature;
        hiddenDiv.style.position = 'absolute';
        hiddenDiv.style.left = '-9999px';
        hiddenDiv.style.top = '-9999px';
        hiddenDiv.style.width = '700px';
        hiddenDiv.style.height = '200px';
        hiddenDiv.style.fontSize = '40px';
        hiddenDiv.style.color = '#000';
        hiddenDiv.style.textAlign = 'center';

        const fontMatch = textSignature.match(/font-family:([^;"]+)/);
        const appliedFont = fontMatch ? fontMatch[1].trim() : selectedFont;
        hiddenDiv.style.fontFamily = appliedFont;

        document.body.appendChild(hiddenDiv);

        try {
            const canvas = await html2canvas(hiddenDiv, { backgroundColor: null });
            const dataUrl = canvas.toDataURL('image/png');
            setSignaturePreview(dataUrl);
            setNewSignatureFile(null);
            setSignatureModified(true);

            // Force HTML preview mode for live preview
            if (previewMode === 'pdf') {
                setPreviewMode('html');
            }

            toast.success(t('TextSignatureGenerated') || 'Text signature generated successfully');
        } catch (error) {
            console.error('Error rendering text to image:', error);
            toast.error(t('ErrorGeneratingTextSignature') || 'Error generating text signature');
        } finally {
            document.body.removeChild(hiddenDiv);
        }
    };



    const handleSave = async () => {
        try {
            setLoading(true);
            setError('');
            setSuccess('');

            // Handle signature changes (upload, clear, or no change)
            if (signatureModified) {
                // Check if signature was cleared (empty string and no file)
                if (signaturePreview === '' && !newSignatureFile) {
                    // Clear the signature on the backend
                    const response = await ClientService.ClearSignature(token, caseId);
                    if (response.ok) {
                        // Reset modification flags
                        setSignatureModified(false);
                        setNewSignatureFile(null);
                        setSignaturePreview('');
                        toast.success(t('SignatureClearedSuccessfully') || 'Signature cleared successfully');
                    } else {
                        toast.error(t('ErrorClearingSignature') || 'Error clearing signature');
                    }
                } else if (newSignatureFile || (signaturePreview && signaturePreview !== '')) {
                    // Upload new signature (either file upload, drawn signature, or text signature)
                    let fileToUpload;

                    if (newSignatureFile) {
                        // Handle uploaded file
                        if (typeof newSignatureFile === 'string' && newSignatureFile.startsWith('data:image/')) {
                            const base64Data = newSignatureFile.split(',')[1];
                            const binaryData = atob(base64Data);
                            const arrayBuffer = new Uint8Array(binaryData.length);
                            for (let i = 0; i < binaryData.length; i++) {
                                arrayBuffer[i] = binaryData.charCodeAt(i);
                            }
                            const blob = new Blob([arrayBuffer], { type: 'image/png' });
                            fileToUpload = new File([blob], 'signature.png', { type: 'image/png' });
                        } else {
                            fileToUpload = newSignatureFile;
                        }
                    } else if (signaturePreview && signaturePreview.startsWith('data:image/')) {
                        // Handle drawn signature or text signature (data URL)
                        const base64Data = signaturePreview.split(',')[1];
                        const binaryData = atob(base64Data);
                        const arrayBuffer = new Uint8Array(binaryData.length);
                        for (let i = 0; i < binaryData.length; i++) {
                            arrayBuffer[i] = binaryData.charCodeAt(i);
                        }
                        const blob = new Blob([arrayBuffer], { type: 'image/png' });
                        fileToUpload = new File([blob], 'signature.png', { type: 'image/png' });
                    }

                    if (fileToUpload) {
                        const response = await ClientService.UploadSignature(token, accountantId, caseId, fileToUpload);
                        if (response.ok) {
                            // Parse response to get the pre-signed URL
                            const data = await response.json();
                            if (data.url) {
                                // Update preview with the new URL from server
                                setSignaturePreview(data.url);
                            }
                            // Reset modification flags
                            setSignatureModified(false);
                            setNewSignatureFile(null);
                            toast.success(t('SignatureUploadedSuccessfully'));
                        } else {
                            toast.error(t('ErrorUploadingSignatureFiles'));
                        }
                    }
                }
            }

            // Upload profile picture if there's a new one
            if (profileModified && (newProfileFile || (profilePreview && profilePreview !== ''))) {
                let fileToUpload = newProfileFile;

                if (!fileToUpload && profilePreview && profilePreview.startsWith('data:image/')) {
                    // Handle profile picture that might be a data URL
                    const base64Data = profilePreview.split(',')[1];
                    const binaryData = atob(base64Data);
                    const arrayBuffer = new Uint8Array(binaryData.length);
                    for (let i = 0; i < binaryData.length; i++) {
                        arrayBuffer[i] = binaryData.charCodeAt(i);
                    }
                    const blob = new Blob([arrayBuffer], { type: 'image/png' });
                    fileToUpload = new File([blob], 'profile.png', { type: 'image/png' });
                }

                if (fileToUpload) {
                    const response = await ClientService.UploadProfilePhoto(
                        token,
                        accountantId,
                        caseId,
                        fileToUpload
                    );
                    if (response.ok) {
                        // Parse response to get the pre-signed URL
                        const data = await response.json();
                        if (data.url) {
                            // Update preview with the new URL from server
                            setProfilePreview(data.url);
                        }
                        // Reset modification flags
                        setProfileModified(false);
                        setNewProfileFile(null);
                        toast.success(t('ProfilePhotoUploadedSuccessfully'));
                    } else {
                        toast.error(t('ErrorUploadingProfileFiles'));
                    }
                } else {
                    console.log('No profile file to upload');
                }
            }

            let activeSettings = settings;

            // Upload page background if changed (S3 key is persisted inside the version snapshot)
            if (pageBackgroundModified) {
                if (newPageBackgroundFile) {
                    const bgResponse = await ClientService.UploadPageBackground(
                        token,
                        accountantId,
                        caseId,
                        newPageBackgroundFile
                    );
                    if (bgResponse.ok) {
                        const bgData = await bgResponse.json();
                        const bgKey = bgData.key || bgData.url || '';
                        const bgPreview = bgData.url || bgData.key || '';
                        activeSettings = {
                            ...activeSettings,
                            columnSettings: {
                                ...activeSettings.columnSettings,
                                pageBackgroundImage: bgPreview,
                                pageBackgroundImageKey: bgKey,
                            },
                        };
                        setNewPageBackgroundFile(null);
                        setPageBackgroundModified(false);
                        toast.success(t('PageBackgroundUploaded') || 'רקע המסמך הועלה בהצלחה');
                    } else {
                        toast.error(t('ErrorUploadingPageBackground') || 'שגיאה בהעלאת רקע המסמך');
                    }
                } else {
                    // Cleared
                    activeSettings = {
                        ...activeSettings,
                        columnSettings: {
                            ...activeSettings.columnSettings,
                            pageBackgroundImage: '',
                            pageBackgroundImageKey: '',
                        },
                    };
                    setPageBackgroundModified(false);
                }
            }

            await persistActiveTarget(activeSettings);
            skipDirtyRef.current = activeSettings !== settings;
            setSettings(activeSettings);
            setIsDirty(false);

            setSuccess(t('DocumentSettingsSavedSuccessfully'));

            // Call parent's onSave callback to refresh client data
            if (onSave) {
                await onSave();
            }


        } catch (err) {
            console.error('Error saving document settings:', err);
            setError(t('ErrorSavingDocumentSettings'));
        } finally {
            setLoading(false);
        }
    };

    const handleSettingChange = (field, value) => {
        setSettings(prev => ({
            ...prev,
            [field]: value
        }));

        // Force HTML preview mode when making changes for live preview
        if (previewMode === 'pdf') {
            setPreviewMode('html');
        }
    };

    const handleColumnSettingChange = (field, value) => {
        setSettings(prev => {
            const newSettings = {
                ...prev,
                columnSettings: {
                    ...prev.columnSettings,
                    [field]: value
                }
            };

            // Force HTML preview mode when making changes for live preview
            if (previewMode === 'pdf') {
                setPreviewMode('html');
            }

            return newSettings;
        });
    };

    const tr = (key, fallback) => {
        const value = t(key);
        return value && value !== key ? value : fallback;
    };

    const designTargetStorageKey = `docDesignTarget:${caseId}`;

    // Saves only the design being edited: the base DocumentSettings row or one version row
    const persistActiveTarget = async (activeSettings) => {
        if (activeTargetId === BASE_DESIGN_ID) {
            await UpdateDocumentSettings({
                caseId,
                accountantId,
                ...buildDocumentSettingsPayload(activeSettings),
            }, token);
            setBaseDesign(activeSettings);
            return;
        }
        const row = designVersions.find((v) => v.id === activeTargetId);
        const saved = await UpdateDesignVersion({
            id: activeTargetId,
            caseId,
            name: row?.name || '',
            ...settingsToDesignPayload(activeSettings),
        }, token);
        setDesignVersions((prev) => prev.map((v) => (v.id === activeTargetId ? saved : v)));
    };

    // Background uploads belong to the design being edited and unsaved edits are dropped on switch
    const ensureCanLeaveActiveTarget = async () => {
        if (pageBackgroundModified) {
            toast.warning(tr('SavePageBackgroundBeforeSwitching', 'שמור את רקע המסמך לפני מעבר לגרסה אחרת'));
            return false;
        }
        if (!isDirty) return true;
        const result = await Swal.fire({
            title: tr('UnsavedChanges', 'לא נשמר'),
            text: tr('DiscardDesignChangesConfirm', 'השינויים שלא נשמרו בגרסה זו יאבדו. להמשיך?'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#304fff',
            confirmButtonText: t('Confirm') || 'OK',
            cancelButtonText: t('Cancel') || 'Cancel',
            reverseButtons: true,
            didOpen: (popup) => { popup.parentElement.style.zIndex = 2000; },
        });
        return result.isConfirmed;
    };

    // Load the base design (null) or a version row into the editor
    const loadTarget = (targetId, rows = designVersions, base = baseDesign) => {
        const row = targetId === BASE_DESIGN_ID ? null : rows.find((v) => v.id === targetId);
        const next = row ? designToSettings(row, base?.columnSettings?.headerDetails) : base;
        if (!next) return;
        skipDirtyRef.current = true;
        setSettings({ ...next });
        setActiveTargetId(row ? row.id : BASE_DESIGN_ID);
        setIsDirty(false);
        setNewPageBackgroundFile(null);
        setPageBackgroundModified(false);
        try {
            localStorage.setItem(designTargetStorageKey, row ? row.id : '');
        } catch { /* storage unavailable */ }
        if (previewMode === 'pdf') {
            setPreviewMode('html');
        }
    };

    const handleSelectVersion = async (targetId) => {
        if (targetId === activeTargetId || !(await ensureCanLeaveActiveTarget())) return;
        loadTarget(targetId);
    };

    const promptVersionName = async (title, initialValue) => {
        const { value, isConfirmed } = await Swal.fire({
            title,
            input: 'text',
            inputValue: initialValue,
            showCancelButton: true,
            confirmButtonText: t('Save') || 'Save',
            cancelButtonText: t('Cancel') || 'Cancel',
            confirmButtonColor: '#304fff',
            reverseButtons: true,
            didOpen: (popup) => {
                popup.parentElement.style.zIndex = 2000;
                popup.setAttribute('dir', direction);
            },
            inputValidator: (v) => (!v || !v.trim()
                ? (tr('DesignVersionNameRequired', 'יש להזין שם לגרסה'))
                : undefined),
        });
        return isConfirmed ? value.trim() : null;
    };

    const currentLayoutId = settings.columnSettings?.documentStyleVersion || 'classic';
    // Adds a created/duplicated row and opens it in the editor
    const openCreatedVersion = (created) => {
        const rows = [...designVersions, created];
        setDesignVersions(rows);
        loadTarget(created.id, rows);
        toast.success(tr('DesignVersionCreated', 'הגרסה נוצרה'));
    };

    // New version: pick its layout first, then name it
    const handleCreateVersion = () => {
        if (pageBackgroundModified) {
            toast.warning(tr('SavePageBackgroundBeforeSwitching', 'שמור את רקע המסמך לפני מעבר לגרסה אחרת'));
            return;
        }
        setNewVersionStyleId(currentLayoutId);
    };

    // New version = what is on screen moved to the chosen layout (also one already used by other versions)
    const handleConfirmNewVersionLayout = async () => {
        const styleId = newVersionStyleId;
        setNewVersionStyleId(null);
        if (!styleId) return;
        const sameThemeCount = designVersions.filter((v) => v.documentStyleVersion === styleId).length;
        const name = await promptVersionName(
            tr('NewDesignVersion', 'גרסה חדשה'),
            `${getStyleDisplayName(styleId, t)} ${sameThemeCount + 1}`
        );
        if (!name) return;
        const source = styleId === currentLayoutId
            ? settings
            : { ...settings, columnSettings: applyStyleVersionToColumnSettings(settings.columnSettings, styleId) };
        try {
            setLoading(true);
            openCreatedVersion(await CreateDesignVersion({ caseId, name, ...settingsToDesignPayload(source) }, token));
        } catch (err) {
            console.error('Error creating design version:', err);
            toast.error(tr('ErrorSavingDocumentSettings', 'שגיאה בשמירה'));
        } finally {
            setLoading(false);
        }
    };

    const handleRenameVersion = async (versionId) => {
        const version = designVersions.find((v) => v.id === versionId);
        if (!version) return;
        const name = await promptVersionName(tr('RenameDesignVersion', 'שנה שם'), version.name);
        if (!name || name === version.name) return;
        try {
            setLoading(true);
            const saved = await UpdateDesignVersion({ ...version, caseId, name }, token);
            setDesignVersions((prev) => prev.map((v) => (v.id === versionId ? saved : v)));
        } catch (err) {
            console.error('Error renaming design version:', err);
            toast.error(tr('ErrorSavingDocumentSettings', 'שגיאה בשמירה'));
        } finally {
            setLoading(false);
        }
    };

    const handleDuplicateVersion = async (versionId) => {
        if (pageBackgroundModified) {
            toast.warning(tr('SavePageBackgroundBeforeSwitching', 'שמור את רקע המסמך לפני מעבר לגרסה אחרת'));
            return;
        }
        const source = designVersions.find((v) => v.id === versionId);
        if (!source) return;
        const name = await promptVersionName(
            tr('DuplicateDesignVersion', 'שכפל'),
            `${source.name} (${tr('Copy', 'עותק')})`
        );
        if (!name) return;
        try {
            setLoading(true);
            // The active version is copied with its on-screen (possibly unsaved) state
            const dto = versionId === activeTargetId
                ? { caseId, name, ...settingsToDesignPayload(settings) }
                : { caseId, name, sourceVersionId: versionId };
            openCreatedVersion(await CreateDesignVersion(dto, token));
        } catch (err) {
            console.error('Error duplicating design version:', err);
            toast.error(tr('ErrorSavingDocumentSettings', 'שגיאה בשמירה'));
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteVersion = async (versionId) => {
        const version = designVersions.find((v) => v.id === versionId);
        if (!version) return;
        const result = await Swal.fire({
            title: t('confirmDeleteTitle') || 'Confirm Delete',
            text: `${tr('DeleteDesignVersionConfirm', 'למחוק את הגרסה')} "${version?.name || ''}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('YesDelete') || 'Yes, Delete',
            cancelButtonText: t('Cancel') || 'Cancel',
            reverseButtons: true,
            didOpen: (popup) => { popup.parentElement.style.zIndex = 2000; },
        });
        if (!result.isConfirmed) return;

        try {
            setLoading(true);
            await DeleteDesignVersion(caseId, versionId, token);
            const remaining = designVersions.filter((v) => v.id !== versionId);
            setDesignVersions(remaining);
            if (versionId === activeTargetId) {
                loadTarget(BASE_DESIGN_ID, remaining);
            }
            toast.success(tr('DesignVersionDeleted', 'הגרסה נמחקה'));
        } catch (err) {
            console.error('Error deleting design version:', err);
            if (err?.status === 409) {
                toast.error(tr('DesignVersionInUse', 'לא ניתן למחוק גרסה שכבר הופקו איתה מסמכים'));
            } else {
                toast.error(tr('ErrorSavingDocumentSettings', 'שגיאה בשמירה'));
            }
        } finally {
            setLoading(false);
        }
    };

    // Applies the theme to the design being edited only; never creates or switches versions.
    // A saved version keeps its layout (edit or duplicate it; other layouts start from the base).
    const handleDocumentStyleSelect = (styleId) => {
        if (activeTargetId !== BASE_DESIGN_ID && styleId !== currentLayoutId) return;
        setSettings((prev) => ({
            ...prev,
            columnSettings: applyStyleVersionToColumnSettings(prev.columnSettings, styleId),
        }));
        if (previewMode === 'pdf') {
            setPreviewMode('html');
        }
    };

    const handleHeaderDetailsChange = (headerDetails) => {
        handleColumnSettingChange('headerDetails', headerDetails);
    };

    const handleResetHeaderFromBusiness = async () => {
        const result = await Swal.fire({
            title: tr('ResetHeaderFromBusiness', 'טען מחדש מפרטי העסק'),
            text: tr('ResetHeaderFromBusinessConfirm', 'הערכים בכותרת של גרסה זו יוחלפו בפרטי העסק הנוכחיים.'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#304fff',
            confirmButtonText: t('Confirm') || 'OK',
            cancelButtonText: t('Cancel') || 'Cancel',
            reverseButtons: true,
            didOpen: (popup) => { popup.parentElement.style.zIndex = 2000; },
        });
        if (!result.isConfirmed) return;
        handleHeaderDetailsChange(buildHeaderDetailsFromClient(client, getDefaultHeaderLabels(t, direction)));
    };

    const handlePageBackgroundUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type?.startsWith('image/')) {
            toast.error(t('PleaseSelectImageFile') || 'בחר קובץ תמונה');
            return;
        }
        const objectUrl = URL.createObjectURL(file);
        setSettings((prev) => ({
            ...prev,
            columnSettings: {
                ...prev.columnSettings,
                pageBackgroundImage: objectUrl,
                pageBackgroundOffsetX: 0,
                pageBackgroundOffsetY: 0,
                pageBackgroundRotation: 0,
            },
        }));
        setNewPageBackgroundFile(file);
        setPageBackgroundModified(true);
        if (previewMode === 'pdf') {
            setPreviewMode('html');
        }
    };

    const handleClearPageBackground = () => {
        setSettings((prev) => ({
            ...prev,
            columnSettings: {
                ...prev.columnSettings,
                pageBackgroundImage: '',
                pageBackgroundImageKey: '',
                pageBackgroundOffsetX: 0,
                pageBackgroundOffsetY: 0,
                pageBackgroundRotation: 0,
            },
        }));
        setNewPageBackgroundFile(null);
        setPageBackgroundModified(true);
        if (previewMode === 'pdf') {
            setPreviewMode('html');
        }
    };

    const clampBgOffset = (value) => Math.min(50, Math.max(-50, value));
    const clampBgRotation = (value) => Math.min(180, Math.max(-180, value));

    const handleBgDragStart = (e) => {
        if (!settings.columnSettings?.pageBackgroundImage) return;
        bgDraggingRef.current = true;
        bgDragLastRef.current = { x: e.clientX, y: e.clientY };
        e.currentTarget.setPointerCapture?.(e.pointerId);
    };

    const handleBgDragMove = (e) => {
        if (!bgDraggingRef.current) return;
        const box = bgDragRef.current;
        if (!box) return;
        const rect = box.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const dx = e.clientX - bgDragLastRef.current.x;
        const dy = e.clientY - bgDragLastRef.current.y;
        bgDragLastRef.current = { x: e.clientX, y: e.clientY };
        const pctX = (dx / rect.width) * 100;
        const pctY = (dy / rect.height) * 100;
        setSettings((prev) => ({
            ...prev,
            columnSettings: {
                ...prev.columnSettings,
                pageBackgroundOffsetX: clampBgOffset((prev.columnSettings.pageBackgroundOffsetX || 0) + pctX),
                pageBackgroundOffsetY: clampBgOffset((prev.columnSettings.pageBackgroundOffsetY || 0) + pctY),
            },
        }));
        if (previewMode === 'pdf') {
            setPreviewMode('html');
        }
    };

    const handleBgDragEnd = (e) => {
        bgDraggingRef.current = false;
        try {
            e.currentTarget.releasePointerCapture?.(e.pointerId);
        } catch (_) { /* ignore */ }
    };



    const currentSettings = settings;

    const renderSignatureSection = () => (
                <Grid container spacing={2} dir={direction}>
                    {/* Signature Section */}
                    <Grid item xs={12} md={6} dir={direction}>
                        <Box sx={{
                            p: 1,
                            height: '100%',
                            boxSizing: 'border-box',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '12px',
                            border: '1px solid #e8eaed'
                        }} dir={direction}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }} dir={direction}>
                                <Typography variant="h6" sx={{
                                    color: '#202124',
                                    fontSize: '16px',
                                    fontWeight: 600
                                }} dir={direction}>
                                    {t('Signature')}
                                </Typography>
                                {signaturePreview && (
                                    <Tooltip title={t('RemoveSignature') || 'Remove signature'}>
                                        <IconButton
                                            size="small"
                                            onClick={handleRemoveSignature}
                                            sx={{
                                                color: '#d33',
                                                backgroundColor: '#EBEBEB',
                                                '&:hover': {
                                                    backgroundColor: '#D0D0D0'
                                                }
                                            }}
                                            aria-label="remove signature"
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>

                            {signaturePreview && (
                                <Box sx={{ mb: 2, textAlign: 'center' }} dir={direction}>
                                    <img
                                        src={signaturePreview}
                                        alt="signature-preview"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '80px',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '4px',
                                            cursor: 'pointer'
                                        }}
                                    />
                                </Box>
                            )}

                            {addSignaturePad && (
                                <Box sx={{ mb: 1 }} dir={direction}>
                                    <SignaturePad
                                        ref={sigPad}
                                        canvasProps={{
                                            style: {
                                                border: '1px solid #e0e0e0',
                                                borderRadius: '4px',
                                                width: '100%',
                                                height: '120px',
                                                backgroundColor: '#ffffff'
                                            },
                                        }}
                                        onEnd={handleSignatureChange}
                                    />
                                    <StyledButton
                                        variant="outlined"
                                        onClick={handleClearSignature}
                                        sx={{ mt: 2 }}
                                        dir={direction}
                                        size="small"
                                    >
                                        {t('ClearSignature')}
                                    </StyledButton>
                                </Box>
                            )}

                            <Box className={styles.signatureActions} dir={direction}>
                                <StyledButton
                                    variant="contained"
                                    component="label"
                                    size="medium"
                                    startIcon={<UploadIcon />}
                                    dir={direction}
                                    sx={cardActionButtonSx}
                                >
                                    <span className={styles.buttonLabel}>{t('UploadSignature')}</span>
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        onChange={handleSignatureUpload}
                                    />
                                </StyledButton>
                                <StyledButton
                                    variant={addSignaturePad ? 'contained' : 'outlined'}
                                    size="medium"
                                    startIcon={<DrawIcon />}
                                    onClick={() => setAddSignaturePad(!addSignaturePad)}
                                    dir={direction}
                                    aria-pressed={addSignaturePad}
                                    sx={cardActionButtonSx}
                                >
                                    <span className={styles.buttonLabel}>{t('DrawSignature')}</span>
                                </StyledButton>
                                <StyledButton
                                    variant={textSignatureOption ? 'contained' : 'outlined'}
                                    size="medium"
                                    startIcon={<TextIncreaseIcon />}
                                    onClick={() => setTextSignatureOption(!textSignatureOption)}
                                    dir={direction}
                                    aria-pressed={textSignatureOption}
                                    sx={cardActionButtonSx}
                                >
                                    <span className={styles.buttonLabel}>{t('TextSignature')}</span>
                                </StyledButton>
                            </Box>

                            {/* Signature Size Slider */}
                            <Box sx={{ mt: 2 }} dir={direction}>
                                <div className={styles.sliderField}>
                                    <div className={styles.sliderLabelRow}>
                                        <span>{t('SignatureSize') || 'גודל חתימה'}</span>
                                        <span className={styles.sliderValue}>
                                            {currentSettings.columnSettings.signatureWidth || 80}mm × {currentSettings.columnSettings.signatureHeight || 40}mm
                                        </span>
                                    </div>
                                    <StyledSlider
                                        size="small"
                                        direction={direction}
                                        value={currentSettings.columnSettings.signatureWidth || 80}
                                        onChange={(e, newValue) => {
                                            handleColumnSettingChange('signatureWidth', newValue);
                                            handleColumnSettingChange('signatureHeight', Math.round(newValue * 0.5));
                                        }}
                                        min={40}
                                        max={150}
                                        step={5}
                                        marks
                                        valueLabelDisplay="auto"
                                    />
                                </div>
                            </Box>

                            {textSignatureOption && (
                                <Box sx={{ mt: 2 }} dir={direction}>
                                    <Box sx={{ mb: 1 }}>
                                        <div className={styles.fieldTitle}>{tr('Font', 'גופן')}</div>
                                        <AutocompleteSelectFilter
                                            options={signatureFontOptions}
                                            selectedValues={pickOption(signatureFontOptions, selectedFont)}
                                            onChange={(_, option) => setSelectedFont(option?.value || 'Arial')}
                                            label={t('Font')}
                                            placeholder={t('Font')}
                                            isMultiple={false}
                                            direction={direction}
                                            portalMenu
                                            searchable
                                        />
                                    </Box>
                                    <ReactQuill
                                        theme="snow"
                                        value={textSignature}
                                        onChange={setTextSignature}
                                        modules={{
                                            toolbar: [
                                                ['bold', 'italic', 'underline'],
                                                [{ 'align': [] }],
                                            ],
                                        }}
                                        style={{ height: '80px', marginBottom: '12px' }}
                                    />
                                    <StyledButton
                                        variant="contained"
                                        onClick={generateTextSignatureImage}
                                        fullWidth
                                        dir={direction}
                                        size="medium"
                                    >
                                        {t('GenerateTextSignature')}
                                    </StyledButton>
                                </Box>
                            )}
                        </Box>
                    </Grid>

                    {/* Profile Picture Section */}
                    <Grid item xs={12} md={6} dir={direction}>
                        <Box sx={{
                            p: 1,
                            height: '100%',
                            boxSizing: 'border-box',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '12px',
                            border: '1px solid #e8eaed'
                        }} dir={direction}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }} dir={direction}>
                                <Typography variant="h6" sx={{
                                    color: '#202124',
                                    fontSize: '16px',
                                    fontWeight: 600
                                }} dir={direction}>
                                    {t('ProfilePicture')}
                                </Typography>
                                {profilePreview && (
                                    <Tooltip title={t('RemoveProfilePicture') || 'Remove profile picture'}>
                                        <IconButton
                                            size="small"
                                            onClick={handleRemoveProfilePicture}
                                            sx={{
                                                color: '#d33',
                                                backgroundColor: '#EBEBEB',
                                                '&:hover': {
                                                    backgroundColor: '#D0D0D0'
                                                }
                                            }}
                                            aria-label="remove profile picture"
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>

                            {profilePreview && (
                                <Box sx={{ mb: 2, textAlign: 'center' }} dir={direction}>
                                    <img
                                        src={profilePreview}
                                        alt="profile-preview"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '80px',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '4px',
                                            cursor: 'pointer'
                                        }}
                                    />
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <StyledButton
                                variant="contained"
                                component="label"
                                fullWidth
                                startIcon={<UploadIcon />}
                                dir={direction}
                                size="medium"
                                sx={cardActionButtonSx}
                            >
                                <span className={styles.buttonLabel}>{t('UploadProfilePicture')}</span>
                                <input
                                    type="file"
                                    hidden
                                    onChange={handleProfilePictureUpload}
                                />
                            </StyledButton>
                            </Box>

                            {/* Profile Picture Size Slider */}
                            <Box sx={{ mt: 2 }} dir={direction}>
                                <div className={styles.sliderField}>
                                    <div className={styles.sliderLabelRow}>
                                        <span>{t('ProfilePictureSize') || 'גודל תמונת פרופיל'}</span>
                                        <span className={styles.sliderValue}>
                                            {currentSettings.columnSettings.width || 80}mm
                                        </span>
                                    </div>
                                    <StyledSlider
                                        size="small"
                                        direction={direction}
                                        value={currentSettings.columnSettings.width || 80}
                                        onChange={(e, newValue) => {
                                            handleColumnSettingChange('width', newValue);
                                            handleColumnSettingChange('height', newValue);
                                        }}
                                        min={40}
                                        max={120}
                                        step={5}
                                        marks
                                        valueLabelDisplay="auto"
                                    />
                                </div>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
    );

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="xl"
                fullWidth
                PaperProps={{ className: styles.dialogPaper }}
                sx={{ direction }}
                dir={direction}
                disableEnforceFocus
            >
                <DialogTitle className={styles.dialogTitle} dir={direction}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} dir={direction}>
                        <SettingsIcon className={styles.dialogTitleIcon} />
                        <Typography variant="h6" className={styles.dialogTitleText} dir={direction}>
                            {t('DocumentSettings')}
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        dir={direction}
                        className={styles.closeBtn}
                        aria-label="close"
                    >
                        <CloseIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </DialogTitle>

                <DialogContent className={styles.dialogContent} dir={direction}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2, m: 2 }} dir={direction}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert severity="success" sx={{ mb: 2, m: 2 }} dir={direction}>
                            {success}
                        </Alert>
                    )}

                    {/* Main Content - Two Column Layout */}
                    <Box className={styles.mainLayout} dir={direction}>
                        {/* Left Panel - Customization Options */}
                        <Box className={styles.settingsPanel}>
                            <div className={styles.tabs} dir={direction}>
                                {settingsTabs.map(({ id, icon: Icon, label }) => (
                                    <button
                                        key={id}
                                        type="button"
                                        className={`${styles.tab}${settingsTab === id ? ` ${styles.tabActive}` : ''}`}
                                        onClick={() => setSettingsTab(id)}
                                    >
                                        <Icon className={styles.tabIcon} />
                                        <span>{label}</span>
                                    </button>
                                ))}
                            </div>

                            <Box className={styles.tabPanel}>
                            {/* Document style versions */}
                            {settingsTab === 'style' && (
                                <Box dir={direction}>
                                    <DesignVersionsPanel
                                        t={t}
                                        direction={direction}
                                        baseStyleId={(activeTargetId === BASE_DESIGN_ID
                                            ? settings.columnSettings?.documentStyleVersion
                                            : baseDesign?.columnSettings?.documentStyleVersion) || 'classic'}
                                        versions={designVersions.map((v) => (v.id === activeTargetId
                                            ? { ...v, documentStyleVersion: currentLayoutId }
                                            : v))}
                                        activeTargetId={activeTargetId}
                                        isDirty={isDirty}
                                        saving={loading}
                                        onSelect={handleSelectVersion}
                                        onCreate={handleCreateVersion}
                                        onRename={handleRenameVersion}
                                        onDuplicate={handleDuplicateVersion}
                                        onDelete={handleDeleteVersion}
                                        onSave={handleSave}
                                    />
                                    <DocumentStylePicker
                                        t={t}
                                        direction={direction}
                                        selectedStyleId={currentLayoutId}
                                        lockedStyleId={activeTargetId !== BASE_DESIGN_ID ? currentLayoutId : null}
                                        onSelect={handleDocumentStyleSelect}
                                        versionCounts={designVersions.reduce((acc, v) => {
                                            acc[v.documentStyleVersion] = (acc[v.documentStyleVersion] || 0) + 1;
                                            return acc;
                                        }, {})}
                                    />
                                </Box>
                            )}

                            {settingsTab === 'signature' && (
                                <Box dir={direction}>
                                    <Box className={styles.sectionBlock}>
                                        <div className={styles.sectionHeading}>
                                            <CreateIcon className={styles.sectionHeadingIcon} />
                                            {t('SignatureAndProfilePicture')}
                                        </div>
                                        {renderSignatureSection()}
                                    </Box>
                                    <Box className={styles.sectionBlock}>
                                        <div className={styles.sectionHeading}>
                                            <WallpaperIcon className={styles.sectionHeadingIcon} />
                                            {t('PageBackgroundImage') || 'תמונת רקע לעמוד'}
                                        </div>

                                    <Typography className={styles.hint} dir={direction}>
                                        {t('PageBackgroundImageHint') ||
                                            'תמונה שקופה על כל עמוד המסמך (סימן מים / נייר מכתבים). מתאים במיוחד לעיצוב "גלים רכים".'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }} dir={direction}>
                                        <StyledButton
                                            variant="outlined"
                                            size="small"
                                            component="label"
                                            startIcon={<UploadIcon />}
                                            dir={direction}
                                        >
                                            {t('UploadBackground') || 'העלה רקע'}
                                            <input
                                                type="file"
                                                hidden
                                                accept="image/*"
                                                onChange={handlePageBackgroundUpload}
                                            />
                                        </StyledButton>
                                        {currentSettings.columnSettings?.pageBackgroundImage && (
                                            <StyledButton
                                                variant="outlined"
                                                size="small"
                                                color="error"
                                                onClick={handleClearPageBackground}
                                                dir={direction}
                                            >
                                                {t('RemoveBackground') || 'הסר רקע'}
                                            </StyledButton>
                                        )}
                                    </Box>
                                    {currentSettings.columnSettings?.pageBackgroundImage && (
                                        <Box sx={{ mb: 1.5 }}>
                                            <Typography className={styles.hint} sx={{ mb: 1 }} dir={direction}>
                                                {t('BackgroundDragHint') || 'גרור את התמונה כדי להזיז אותה בעמוד. השינויים מופיעים מיד בתצוגה המקדימה.'}
                                            </Typography>
                                            <Box
                                                ref={bgDragRef}
                                                onPointerDown={handleBgDragStart}
                                                onPointerMove={handleBgDragMove}
                                                onPointerUp={handleBgDragEnd}
                                                onPointerCancel={handleBgDragEnd}
                                                sx={{
                                                    position: 'relative',
                                                    width: '100%',
                                                    height: 140,
                                                    overflow: 'hidden',
                                                    borderRadius: '10px',
                                                    border: '1px solid #e5e7f6',
                                                    background: '#f5f6fd',
                                                    cursor: 'grab',
                                                    touchAction: 'none',
                                                    userSelect: 'none',
                                                    '&:active': { cursor: 'grabbing' },
                                                }}
                                            >
                                                <Box
                                                    component="img"
                                                    src={currentSettings.columnSettings.pageBackgroundImage}
                                                    alt="page background"
                                                    draggable={false}
                                                    sx={{
                                                        position: 'absolute',
                                                        left: '50%',
                                                        top: '50%',
                                                        width: '140%',
                                                        height: '140%',
                                                        maxWidth: 'none',
                                                        objectFit: 'cover',
                                                        opacity: currentSettings.columnSettings.pageBackgroundOpacity ?? 0.18,
                                                        transform: `translate(calc(-50% + ${currentSettings.columnSettings.pageBackgroundOffsetX || 0}%), calc(-50% + ${currentSettings.columnSettings.pageBackgroundOffsetY || 0}%)) rotate(${currentSettings.columnSettings.pageBackgroundRotation || 0}deg)`,
                                                        transformOrigin: 'center center',
                                                        pointerEvents: 'none',
                                                    }}
                                                />
                                            </Box>
                                            <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                                                <div className={styles.sliderField}>
                                                    <div className={styles.sliderLabelRow}>
                                                        <span>{t('BackgroundOpacity') || 'שקיפות רקע'}</span>
                                                        <span className={styles.sliderValue}>
                                                            {Math.round((currentSettings.columnSettings.pageBackgroundOpacity ?? 0.18) * 100)}%
                                                        </span>
                                                    </div>
                                                    <StyledSlider
                                                        size="small"
                                                        direction={direction}
                                                        value={Math.round((currentSettings.columnSettings.pageBackgroundOpacity ?? 0.18) * 100)}
                                                        onChange={(e, v) => handleColumnSettingChange('pageBackgroundOpacity', v / 100)}
                                                        min={5}
                                                        max={60}
                                                        step={1}
                                                        marks
                                                        valueLabelDisplay="auto"
                                                        valueLabelFormat={(v) => `${v}%`}
                                                    />
                                                </div>
                                                <div className={styles.sliderField}>
                                                    <div className={styles.sliderLabelRow}>
                                                        <span>{t('BackgroundMoveHorizontal') || 'הזזה אופקית'}</span>
                                                        <span className={styles.sliderValue}>
                                                            {Math.round(currentSettings.columnSettings.pageBackgroundOffsetX || 0)}%
                                                        </span>
                                                    </div>
                                                    <StyledSlider
                                                        size="small"
                                                        direction={direction}
                                                        value={currentSettings.columnSettings.pageBackgroundOffsetX || 0}
                                                        onChange={(e, v) => handleColumnSettingChange('pageBackgroundOffsetX', v)}
                                                        min={-50}
                                                        max={50}
                                                        step={1}
                                                        valueLabelDisplay="auto"
                                                    />
                                                </div>
                                                <div className={styles.sliderField}>
                                                    <div className={styles.sliderLabelRow}>
                                                        <span>{t('BackgroundMoveVertical') || 'הזזה אנכית'}</span>
                                                        <span className={styles.sliderValue}>
                                                            {Math.round(currentSettings.columnSettings.pageBackgroundOffsetY || 0)}%
                                                        </span>
                                                    </div>
                                                    <StyledSlider
                                                        size="small"
                                                        direction={direction}
                                                        value={currentSettings.columnSettings.pageBackgroundOffsetY || 0}
                                                        onChange={(e, v) => handleColumnSettingChange('pageBackgroundOffsetY', v)}
                                                        min={-50}
                                                        max={50}
                                                        step={1}
                                                        valueLabelDisplay="auto"
                                                    />
                                                </div>
                                                <div className={styles.sliderField}>
                                                    <div className={styles.sliderLabelRow}>
                                                        <span>{t('BackgroundRotation') || 'סיבוב'}</span>
                                                        <span className={styles.sliderValue}>
                                                            {Math.round(currentSettings.columnSettings.pageBackgroundRotation || 0)}°
                                                        </span>
                                                    </div>
                                                    <StyledSlider
                                                        size="small"
                                                        direction={direction}
                                                        value={currentSettings.columnSettings.pageBackgroundRotation || 0}
                                                        onChange={(e, v) => handleColumnSettingChange('pageBackgroundRotation', v)}
                                                        min={-180}
                                                        max={180}
                                                        step={1}
                                                        valueLabelDisplay="auto"
                                                        valueLabelFormat={(v) => `${v}°`}
                                                    />
                                                </div>
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }} dir={direction}>
                                                    <StyledButton
                                                        variant="outlined"
                                                        size="small"
                                                        dir={direction}
                                                        onClick={() => {
                                                            handleColumnSettingChange('pageBackgroundOffsetX', 0);
                                                            handleColumnSettingChange('pageBackgroundOffsetY', 0);
                                                            handleColumnSettingChange('pageBackgroundRotation', 0);
                                                        }}
                                                    >
                                                        {t('ResetBackgroundPosition') || 'איפוס מיקום'}
                                                    </StyledButton>
                                                    <StyledButton
                                                        variant="outlined"
                                                        size="small"
                                                        dir={direction}
                                                        onClick={() => handleColumnSettingChange(
                                                            'pageBackgroundRotation',
                                                            clampBgRotation((currentSettings.columnSettings.pageBackgroundRotation || 0) - 90)
                                                        )}
                                                    >
                                                        ↺ 90°
                                                    </StyledButton>
                                                    <StyledButton
                                                        variant="outlined"
                                                        size="small"
                                                        dir={direction}
                                                        onClick={() => handleColumnSettingChange(
                                                            'pageBackgroundRotation',
                                                            clampBgRotation((currentSettings.columnSettings.pageBackgroundRotation || 0) + 90)
                                                        )}
                                                    >
                                                        ↻ 90°
                                                    </StyledButton>
                                                </Box>
                                            </Box>
                                        </Box>
                                    )}
                                    </Box>
                                </Box>
                            )}

                            {/* Column Settings */}
                            {settingsTab === 'columns' && (
                                <Box dir={direction}>
                                    <div className={styles.sectionRow}>
                                        <Box className={styles.sectionBlock}>
                                            <div className={styles.sectionHeading}>
                                                <BusinessCenterIcon className={styles.sectionHeadingIcon} />
                                                {t('DefaultItemForDocuments') || 'פריט ברירת מחדל למסמכים'}
                                            </div>
                                            <Typography className={styles.DefaultItemDescription} dir={direction}>
                                                {t('DefaultItemDescription') || 'בחר פריט שיופיע אוטומטית בטבלת הפריטים בעת יצירת חשבונית/קבלה.'}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }} dir={direction}>
                                                <Box sx={{ flex: '1 1 160px', minWidth: 160 }}>
                                                    <AutocompleteSelectFilter
                                                        options={defaultItemSelectOptions}
                                                        selectedValues={pickOption(defaultItemSelectOptions, selectedItemMakat || '')}
                                                        onChange={(_, option) => {
                                                            const value = option?.value ?? '';
                                                            if (value === '__add_client_item__') {
                                                                setCreateItemModalOpen(true);
                                                                return;
                                                            }
                                                            setSelectedItemMakat(value);
                                                        }}
                                                        label={t('SelectItem') || 'בחר פריט'}
                                                        placeholder={t('SelectItem') || 'בחר פריט'}
                                                        isMultiple={false}
                                                        direction={direction}
                                                        portalMenu
                                                        searchable
                                                    />
                                                </Box>
                                                <StyledButton
                                                    variant="contained"
                                                    size="small"
                                                    onClick={handleSetDefaultItem}
                                                    disabled={defaultItemLoading}
                                                    dir={direction}
                                                >
                                                    {defaultItemLoading ? t('Saving') : (t('SetAsDefault') || 'הגדר כברירת מחדל')}
                                                </StyledButton>
                                            </Box>
                                        </Box>

                                        <Box className={styles.sectionBlock}>
                                            <div className={styles.sectionHeading}>
                                                <BusinessCenterIcon className={styles.sectionHeadingIcon} />
                                                {t('BusinessServicesSettings')}
                                            </div>
                                            <div className={styles.fieldTitle}>{tr('ItemsDisplayMode', 'אופן הצגת פריטים')}</div>
                                            <AutocompleteSelectFilter
                                                options={itemsDisplayModeOptions}
                                                selectedValues={pickOption(itemsDisplayModeOptions, currentSettings.itemsDisplayMode || 'normal')}
                                                onChange={(_, option) => handleSettingChange('itemsDisplayMode', option?.value || 'normal')}
                                                label={t('ItemsDisplayMode')}
                                                placeholder={t('ItemsDisplayMode')}
                                                isMultiple={false}
                                                direction={direction}
                                                portalMenu
                                            />
                                        </Box>
                                    </div>

                                    <Box className={styles.sectionBlock}>
                                        <div className={styles.sectionHeading}>
                                            <ViewColumnIcon className={styles.sectionHeadingIcon} />
                                            {t('ColumnSettings')}
                                        </div>
                                        <Grid container spacing={1}>
                                        <Grid item xs={12} >
                                            <Divider>
                                                <Chip label={t('SectionTextSizes') || 'גודל וצבע לפי אזור'} size="small" />
                                            </Divider>
                                            <Typography className={styles.hint} sx={{ mt: 0.5, mb: 0 }} dir={direction}>
                                                {t('SectionTextSizesHint') || 'כל אזור במסמך עם גודל וצבע משלו — לא משפיע על שאר האזורים.'}
                                            </Typography>
                                        </Grid>

                                        {/* Company info section */}
                                        <Grid item xs={12} md={6}>
                                            <Box className={styles.sectionBlock} sx={{ borderBottom: 'none', mb: 0, pb: 0 }}>
                                                <div className={styles.sectionHeading}>
                                                    {t('SectionCompanyInfo') || 'פרטי עסק'}
                                                </div>
                                                <div className={styles.sliderField}>
                                                    <div className={styles.sliderLabelRow}>
                                                        <span>{t('FontSize') || 'גודל גופן'}</span>
                                                        <span className={styles.sliderValue}>{currentSettings.columnSettings.companyInfoTextSize}px</span>
                                                    </div>
                                                    <StyledSlider
                                                        size="small"
                                                        direction={direction}
                                                        value={currentSettings.columnSettings.companyInfoTextSize}
                                                        onChange={(e, newValue) => handleColumnSettingChange('companyInfoTextSize', newValue)}
                                                        min={12}
                                                        max={24}
                                                        step={1}
                                                        marks
                                                        valueLabelDisplay="auto"
                                                    />
                                                </div>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }} dir={direction}>
                                                    <input
                                                        type="color"
                                                        value={currentSettings.columnSettings.companyInfoTextColor || '#000000'}
                                                        onChange={(e) => handleColumnSettingChange('companyInfoTextColor', e.target.value)}
                                                        style={{ width: 36, height: 36, border: 'none', borderRadius: 8, cursor: 'pointer' }}
                                                    />
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        value={currentSettings.columnSettings.companyInfoTextColor || '#000000'}
                                                        onChange={(e) => handleColumnSettingChange('companyInfoTextColor', e.target.value)}
                                                        label={t('SectionTextColor') || t('TextColor') || 'צבע טקסט'}
                                                    />
                                                </Box>
                                            </Box>
                                        </Grid>

                                        {/* Document title section */}
                                        <Grid item xs={12} md={6}>
                                            <Box className={styles.sectionBlock} sx={{ borderBottom: 'none', mb: 0, pb: 0 }}>
                                                <div className={styles.sectionHeading}>
                                                    {t('SectionDocumentTitle') || 'כותרת מסמך'}
                                                </div>
                                                <div className={styles.sliderField}>
                                                    <div className={styles.sliderLabelRow}>
                                                        <span>{t('FontSize') || 'גודל גופן'}</span>
                                                        <span className={styles.sliderValue}>{currentSettings.columnSettings.documentTitleTextSize}px</span>
                                                    </div>
                                                    <StyledSlider
                                                        size="small"
                                                        direction={direction}
                                                        value={currentSettings.columnSettings.documentTitleTextSize}
                                                        onChange={(e, newValue) => handleColumnSettingChange('documentTitleTextSize', newValue)}
                                                        min={16}
                                                        max={32}
                                                        step={1}
                                                        marks
                                                        valueLabelDisplay="auto"
                                                    />
                                                </div>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }} dir={direction}>
                                                    <input
                                                        type="color"
                                                        value={currentSettings.columnSettings.titleTextColor || '#000000'}
                                                        onChange={(e) => handleColumnSettingChange('titleTextColor', e.target.value)}
                                                        style={{ width: 36, height: 36, border: 'none', borderRadius: 8, cursor: 'pointer' }}
                                                    />
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        value={currentSettings.columnSettings.titleTextColor || '#000000'}
                                                        onChange={(e) => handleColumnSettingChange('titleTextColor', e.target.value)}
                                                        label={t('SectionTextColor') || t('TextColor') || 'צבע טקסט'}
                                                    />
                                                </Box>
                                            </Box>
                                        </Grid>

                                        {/* Client details section */}
                                        <Grid item xs={12} md={6}>
                                            <Box className={styles.sectionBlock} sx={{ borderBottom: 'none', mb: 0, pb: 0 }}>
                                                <div className={styles.sectionHeading}>
                                                    {t('SectionClientDetails') || 'פרטי לקוח'}
                                                </div>
                                                <div className={styles.sliderField}>
                                                    <div className={styles.sliderLabelRow}>
                                                        <span>{t('FontSize') || 'גודל גופן'}</span>
                                                        <span className={styles.sliderValue}>{currentSettings.columnSettings.clientDetailsTextSize ?? 13}px</span>
                                                    </div>
                                                    <StyledSlider
                                                        size="small"
                                                        direction={direction}
                                                        value={currentSettings.columnSettings.clientDetailsTextSize ?? 13}
                                                        onChange={(e, newValue) => handleColumnSettingChange('clientDetailsTextSize', newValue)}
                                                        min={10}
                                                        max={20}
                                                        step={1}
                                                        marks
                                                        valueLabelDisplay="auto"
                                                    />
                                                </div>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }} dir={direction}>
                                                    <input
                                                        type="color"
                                                        value={currentSettings.columnSettings.clientDetailsTextColor || '#000000'}
                                                        onChange={(e) => handleColumnSettingChange('clientDetailsTextColor', e.target.value)}
                                                        style={{ width: 36, height: 36, border: 'none', borderRadius: 8, cursor: 'pointer' }}
                                                    />
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        value={currentSettings.columnSettings.clientDetailsTextColor || '#000000'}
                                                        onChange={(e) => handleColumnSettingChange('clientDetailsTextColor', e.target.value)}
                                                        label={t('SectionTextColor') || t('TextColor') || 'צבע טקסט'}
                                                    />
                                                </Box>
                                            </Box>
                                        </Grid>

                                        {/* Items table section */}
                                        <Grid item xs={12} md={6}>
                                            <Box className={styles.sectionBlock} sx={{ borderBottom: 'none', mb: 0, pb: 0 }}>
                                                <div className={styles.sectionHeading}>
                                                    {t('SectionItemsTable') || 'טבלת פריטים'}
                                                </div>
                                                <div className={styles.sliderField}>
                                                    <div className={styles.sliderLabelRow}>
                                                        <span>{t('FontSize') || 'גודל גופן'}</span>
                                                        <span className={styles.sliderValue}>{currentSettings.columnSettings.tableTextSize ?? 11}px</span>
                                                    </div>
                                                    <StyledSlider
                                                        size="small"
                                                        direction={direction}
                                                        value={currentSettings.columnSettings.tableTextSize ?? 11}
                                                        onChange={(e, newValue) => {
                                                            handleColumnSettingChange('tableTextSize', newValue);
                                                            handleColumnSettingChange('textSize', newValue);
                                                        }}
                                                        min={8}
                                                        max={16}
                                                        step={1}
                                                        marks
                                                        valueLabelDisplay="auto"
                                                    />
                                                </div>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }} dir={direction}>
                                                    <input
                                                        type="color"
                                                        value={currentSettings.columnSettings.tableTextColor || currentSettings.columnSettings.textColor || '#000000'}
                                                        onChange={(e) => {
                                                            handleColumnSettingChange('tableTextColor', e.target.value);
                                                            handleColumnSettingChange('textColor', e.target.value);
                                                        }}
                                                        style={{ width: 36, height: 36, border: 'none', borderRadius: 8, cursor: 'pointer' }}
                                                    />
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        value={currentSettings.columnSettings.tableTextColor || currentSettings.columnSettings.textColor || '#000000'}
                                                        onChange={(e) => {
                                                            handleColumnSettingChange('tableTextColor', e.target.value);
                                                            handleColumnSettingChange('textColor', e.target.value);
                                                        }}
                                                        label={t('SectionTextColor') || t('TextColor') || 'צבע טקסט'}
                                                    />
                                                </Box>
                                            </Box>
                                        </Grid>

                                        {/* Totals section */}
                                        <Grid item xs={12} md={6}>
                                            <Box className={styles.sectionBlock} sx={{ borderBottom: 'none', mb: 0, pb: 0 }}>
                                                <div className={styles.sectionHeading}>
                                                    {t('SectionTotals') || 'סיכומים'}
                                                </div>
                                                <div className={styles.sliderField}>
                                                    <div className={styles.sliderLabelRow}>
                                                        <span>{t('FontSize') || 'גודל גופן'}</span>
                                                        <span className={styles.sliderValue}>{currentSettings.columnSettings.totalsTextSize ?? 13}px</span>
                                                    </div>
                                                    <StyledSlider
                                                        size="small"
                                                        direction={direction}
                                                        value={currentSettings.columnSettings.totalsTextSize ?? 13}
                                                        onChange={(e, newValue) => handleColumnSettingChange('totalsTextSize', newValue)}
                                                        min={10}
                                                        max={20}
                                                        step={1}
                                                        marks
                                                        valueLabelDisplay="auto"
                                                    />
                                                </div>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }} dir={direction}>
                                                    <input
                                                        type="color"
                                                        value={currentSettings.columnSettings.totalsTextColor || '#000000'}
                                                        onChange={(e) => handleColumnSettingChange('totalsTextColor', e.target.value)}
                                                        style={{ width: 36, height: 36, border: 'none', borderRadius: 8, cursor: 'pointer' }}
                                                    />
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        value={currentSettings.columnSettings.totalsTextColor || '#000000'}
                                                        onChange={(e) => handleColumnSettingChange('totalsTextColor', e.target.value)}
                                                        label={t('SectionTextColor') || t('TextColor') || 'צבע טקסט'}
                                                    />
                                                </Box>
                                            </Box>
                                        </Grid>

                                        {/* Accent chrome: table header, פריטים, פרטים, סה״כ לתשלום, footer */}
                                        <Grid item xs={12} md={6}>
                                            <Box className={styles.sectionBlock} sx={{ borderBottom: 'none', mb: 0, pb: 0 }}>
                                                <div className={styles.sectionHeading}>
                                                    {t('SectionAccentColor') || 'כותרת טבלה, פריטים, פרטים וסה״כ'}
                                                </div>
                                                <Typography className={styles.hint} sx={{ mt: 0, mb: 1 }} dir={direction}>
                                                    {t('SectionAccentColorHint') || 'צבע לכותרות הטבלה, כותרת פריטים, כותרת פרטים, סה״כ לתשלום והכותרת התחתונה.'}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} dir={direction}>
                                                    <input
                                                        type="color"
                                                        value={currentSettings.columnSettings.accentColor || '#1B3A6B'}
                                                        onChange={(e) => handleColumnSettingChange('accentColor', e.target.value)}
                                                        style={{ width: 36, height: 36, border: 'none', borderRadius: 8, cursor: 'pointer' }}
                                                    />
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        value={currentSettings.columnSettings.accentColor || '#1B3A6B'}
                                                        onChange={(e) => handleColumnSettingChange('accentColor', e.target.value)}
                                                        label={t('SectionTextColor') || t('TextColor') || 'צבע טקסט'}
                                                    />
                                                </Box>
                                            </Box>
                                        </Grid>
                                        {/* Bold Date */}
                                        <Grid item xs={3} dir={direction} sx={{ display: 'flex', alignItems: 'center' }}>
                                            <label
                                                className={`${styles.switchRow} ${styles[direction] || ''}`}
                                                dir={direction}
                                            >
                                                <span>{t('BoldDate')}</span>
                                                <input
                                                    type="checkbox"
                                                    checked={!!currentSettings.columnSettings.boldDate}
                                                    onChange={(e) => handleColumnSettingChange('boldDate', e.target.checked)}
                                                />
                                                <span className={styles.switchTrack} aria-hidden="true" />
                                            </label>
                                        </Grid>
                                        {/* Document font */}
                                        <Grid item xs={3} dir={direction} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                            <div className={styles.fieldTitle}>
                                                {tr('DocumentFont', 'גופן למסמך')}
                                            </div>
                                            <AutocompleteSelectFilter
                                                options={documentFontSelectOptions}
                                                selectedValues={pickOption(documentFontSelectOptions, currentSettings.columnSettings.documentFont || 'Arial')}
                                                onChange={(_, option) => handleColumnSettingChange('documentFont', option?.value || 'Arial')}
                                                label={tr('DocumentFont', 'גופן למסמך')}
                                                placeholder={tr('DocumentFont', 'גופן למסמך')}
                                                isMultiple={false}
                                                direction={direction}
                                                portalMenu
                                                searchable
                                            />
                                        </Grid>

                                        {/* Logo Position */}
                                        <Grid item xs={3} sx={{ mt: 1.5 }}>
                                            <div className={styles.fieldTitle}>{tr('LogoPosition', 'מיקום לוגו')}</div>
                                            <AutocompleteSelectFilter
                                                options={logoPositionOptions}
                                                selectedValues={pickOption(logoPositionOptions, currentSettings.columnSettings.logoPosition || 'left')}
                                                onChange={(_, option) => handleColumnSettingChange('logoPosition', option?.value || 'left')}
                                                label={t('LogoPosition')}
                                                placeholder={t('LogoPosition')}
                                                isMultiple={false}
                                                direction={direction}
                                                portalMenu
                                            />
                                        </Grid>
                                        
                                        {/* Date Alignment */}
                                        <Grid item xs={3} dir={direction} sx={{ mt: 1.5 }}>
                                            <div className={styles.fieldTitle}>{tr('DateAlignment', 'יישור תאריך')}</div>
                                            <AutocompleteSelectFilter
                                                options={dateAlignOptions}
                                                selectedValues={pickOption(dateAlignOptions, currentSettings.columnSettings.dateAlign || 'right')}
                                                onChange={(_, option) => handleColumnSettingChange('dateAlign', option?.value || 'right')}
                                                label={t('DateAlignment') || 'יישור תאריך'}
                                                placeholder={t('DateAlignment') || 'יישור תאריך'}
                                                isMultiple={false}
                                                direction={direction}
                                                portalMenu
                                            />
                                        </Grid>
                                        {/* Totals Layout */}
                                        <Grid item xs={3} dir={direction} sx={{ mt: 1.5 }}>
                                            <div className={styles.fieldTitle}>{tr('TotalsLayout', 'צורת סיכומים לתשלום')}</div>
                                            <AutocompleteSelectFilter
                                                options={totalsLayoutOptions}
                                                selectedValues={pickOption(totalsLayoutOptions, currentSettings.columnSettings.totalsLayout || 'horizontal')}
                                                onChange={(_, option) => handleColumnSettingChange('totalsLayout', option?.value || 'horizontal')}
                                                label={t('TotalsLayout')}
                                                placeholder={t('TotalsLayout')}
                                                isMultiple={false}
                                                direction={direction}
                                                portalMenu
                                            />
                                        </Grid>
                                        {/* Profile Picture Shape */}
                                        <Grid item xs={3} sx={{ mt: 1.5 }}>
                                            <div className={styles.fieldTitle}>{tr('ProfilePictureShape', 'צורת תמונת לוגו')}</div>
                                            <AutocompleteSelectFilter
                                                options={profilePictureShapeOptions}
                                                selectedValues={pickOption(profilePictureShapeOptions, currentSettings.columnSettings.profilePictureShape || 'NoChooseShape')}
                                                onChange={(_, option) => handleColumnSettingChange('profilePictureShape', option?.value || 'NoChooseShape')}
                                                label={t('ProfilePictureShape')}
                                                placeholder={t('ProfilePictureShape')}
                                                isMultiple={false}
                                                direction={direction}
                                                portalMenu
                                                searchable
                                            />
                                        </Grid>

                                        <Grid item xs={12} sx={{ mt: 2 }}>
                                            <Divider>
                                                <Chip label={t('PageColors') || 'צבעי עמוד'} size="small" />
                                            </Divider>
                                            <Typography className={styles.hint} sx={{ mt: 0.5, mb: 0 }} dir={direction}>
                                                {t('PageColorsHint') || 'צבע רקע וצבע כותרת עליונה — לפי העיצוב שנבחר. בכל עיצוב יש ברירת מחדל משלו.'}
                                            </Typography>
                                        </Grid>

                                        {/* Background Color — page (.a4) on every layout */}
                                        <Grid item xs={12} md={6} dir={direction}>
                                            <Box className={styles.sectionBlock} sx={{ borderBottom: 'none', mb: 0, pb: 0 }}>
                                                <div className={styles.sectionHeading}>
                                                    {t('BackgroundColor') || 'צבע רקע'}
                                                </div>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} dir={direction}>
                                                    <input
                                                        type="color"
                                                        value={currentSettings.columnSettings.backgroundColor || '#ffffff'}
                                                        onChange={(e) => handleColumnSettingChange('backgroundColor', e.target.value)}
                                                        style={{ width: 36, height: 36, border: 'none', borderRadius: 8, cursor: 'pointer' }}
                                                    />
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        value={currentSettings.columnSettings.backgroundColor || '#ffffff'}
                                                        onChange={(e) => handleColumnSettingChange('backgroundColor', e.target.value)}
                                                        label={t('SectionTextColor') || t('TextColor') || 'צבע'}
                                                    />
                                                </Box>
                                            </Box>
                                        </Grid>

                                        {/* Header band — classic upper panel / styled banner */}
                                        <Grid item xs={12} md={6} dir={direction}>
                                            <Box className={styles.sectionBlock} sx={{ borderBottom: 'none', mb: 0, pb: 0 }}>
                                                <div className={styles.sectionHeading}>
                                                    {t('HeaderBandColor') || t('DocumentHeader') || 'צבע כותרת עליונה'}
                                                </div>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} dir={direction}>
                                                    <input
                                                        type="color"
                                                        value={currentSettings.columnSettings.upperPanelColor || '#fbfbfb'}
                                                        onChange={(e) => handleColumnSettingChange('upperPanelColor', e.target.value)}
                                                        style={{ width: 36, height: 36, border: 'none', borderRadius: 8, cursor: 'pointer' }}
                                                    />
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        value={currentSettings.columnSettings.upperPanelColor || '#fbfbfb'}
                                                        onChange={(e) => handleColumnSettingChange('upperPanelColor', e.target.value)}
                                                        label={t('SectionTextColor') || t('TextColor') || 'צבע'}
                                                    />
                                                </Box>
                                            </Box>
                                        </Grid>

                                    </Grid>
                                    </Box>
                                </Box>
                            )}

                            {/* Description and Footer */}
                            {settingsTab === 'description' && (
                                <Grid container spacing={2} dir={direction}>
                                    <Grid item xs={12} dir={direction}>
                                        <HeaderDetailsEditor
                                            t={t}
                                            direction={direction}
                                            headerDetails={currentSettings.columnSettings?.headerDetails}
                                            onChange={handleHeaderDetailsChange}
                                            onResetFromBusiness={handleResetHeaderFromBusiness}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6} dir={direction}>
                                        <div className={styles.formRowItem}>
                                            <div className={styles.fieldTitle}>
                                                {t('EnterCustomDescription')}
                                            </div>
                                            <TextField
                                                fullWidth
                                                placeholder={t('EnterCustomDescription')}
                                                value={currentSettings.customDescription}
                                                onChange={(e) => handleSettingChange('customDescription', e.target.value)}
                                                variant="standard"
                                                multiline
                                                minRows={2}
                                                maxRows={8}
                                                size="small"
                                                className={styles.multilineField}
                                                InputProps={{ disableUnderline: true }}
                                                inputProps={{
                                                    style: { textAlign: direction === 'ltr' ? 'left' : 'right' },
                                                }}
                                                dir={direction}
                                            />
                                        </div>
                                    </Grid>
                                    <Grid item xs={12} md={6} dir={direction}>
                                        <div className={styles.formRowItem}>
                                            <div className={styles.fieldTitle}>
                                                {t('EnterCustomFooter')}
                                            </div>
                                            <TextField
                                                fullWidth
                                                placeholder={inheritedCustomFooter || t('EnterCustomFooter')}
                                                helperText={inheritedCustomFooter && !currentSettings.customFooter
                                                    ? tr('CustomFooterInheritedHint', 'משתמש בכותרת התחתונה של העיצוב הבסיסי. הקלידו כאן כדי לשנות אותה לגרסה זו.')
                                                    : undefined}
                                                value={currentSettings.customFooter}
                                                onChange={(e) => handleSettingChange('customFooter', e.target.value)}
                                                variant="standard"
                                                multiline
                                                minRows={2}
                                                maxRows={8}
                                                size="small"
                                                className={styles.multilineField}
                                                InputProps={{ disableUnderline: true }}
                                                inputProps={{
                                                    style: { textAlign: direction === 'ltr' ? 'left' : 'right' },
                                                }}
                                                dir={direction}
                                            />
                                        </div>
                                    </Grid>
                                    <Grid item xs={12} md={6} dir={direction}>
                                        <div className={styles.formRowItem}>
                                            <div className={styles.fieldTitle}>
                                                {t('HeaderRightText') || 'טקסט כותרת ימין'}
                                            </div>
                                            <TextField
                                                fullWidth
                                                placeholder={t('HeaderRightTextPlaceholder') || 'הזן טקסט לכותרת בצד ימין'}
                                                value={currentSettings.headerRightText || ''}
                                                onChange={(e) => handleSettingChange('headerRightText', e.target.value)}
                                                variant="standard"
                                                multiline
                                                minRows={2}
                                                maxRows={6}
                                                size="small"
                                                className={styles.multilineField}
                                                InputProps={{ disableUnderline: true }}
                                                inputProps={{
                                                    style: { textAlign: direction === 'ltr' ? 'left' : 'right' },
                                                }}
                                                dir={direction}
                                            />
                                        </div>
                                    </Grid>
                                    <Grid item xs={12} md={6} dir={direction}>
                                        <div className={styles.formRowItem}>
                                            <div className={styles.fieldTitle}>
                                                {t('HeaderLeftText') || 'טקסט כותרת שמאל'}
                                            </div>
                                            <TextField
                                                fullWidth
                                                placeholder={t('HeaderLeftTextPlaceholder') || 'הזן טקסט לכותרת בצד שמאל'}
                                                value={currentSettings.headerLeftText || ''}
                                                onChange={(e) => handleSettingChange('headerLeftText', e.target.value)}
                                                variant="standard"
                                                multiline
                                                minRows={2}
                                                maxRows={6}
                                                size="small"
                                                className={styles.multilineField}
                                                InputProps={{ disableUnderline: true }}
                                                inputProps={{
                                                    style: { textAlign: direction === 'ltr' ? 'left' : 'right' },
                                                }}
                                                dir={direction}
                                            />
                                        </div>
                                    </Grid>
                                </Grid>
                            )}

                            </Box>
                        </Box>

                        {/* Right Panel - HTML Preview */}
                        <Box className={styles.previewPanel} dir={direction}>
                            <Box className={styles.previewHeader} dir={direction}>
                                <Typography variant="h6" className={styles.previewTitle} dir={direction}>
                                    {t('LivePreview') || 'Live Preview'}
                                </Typography>
                                <Box className={styles.previewActions} dir={direction}>
                                    <StyledButton
                                        variant={previewMode === 'html' ? 'contained' : 'outlined'}
                                        size="small"
                                        onClick={() => setPreviewMode('html')}
                                        dir={direction}
                                    >
                                        {t('LivePreview')}
                                    </StyledButton>
                                    <StyledButton
                                        variant={previewMode === 'pdf' ? 'contained' : 'outlined'}
                                        size="small"
                                        startIcon={<Refresh />}
                                        onClick={() => {
                                            setPreviewMode('pdf');
                                            generatePdfPreview();
                                        }}
                                        disabled={pdfLoading}
                                        dir={direction}
                                    >
                                        {t('PDFPreview')}
                                    </StyledButton>
                                    <Box
                                        variant="contained"
                                        size="small"
                                        onClick={() => openPdfInNewTab(pdfPreview)}
                                        dir={direction}

                                    >
                                        <Tooltip title={t('ClickToOpenInFullScreen') || 'Click to open in full screen'} >
                                            <IconButton
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: '30px',
                                                    height: '30px',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255, 255, 255, 1)',
                                                    },
                                                }}
                                                size="small"
                                                onClick={() => openPdfInNewTab(pdfPreview)}

                                            >
                                                <OpenInNewIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Box>

                            {previewMode === 'html' ? (
                                <DocumentPreview
                                    t={t}
                                    key={`preview-${settings.columnSettings?.documentStyleVersion}-${settings.columnSettings?.pageBackgroundImage ? 'bg' : 'nobg'}-${settings.columnSettings?.pageBackgroundOpacity}-${JSON.stringify(settings.columnSettings)}-${selectedDocumentType}-${signaturePreview ? 'sig' : 'nosig'}-${profilePreview ? 'prof' : 'noprof'}-${defaultItem?.makat ?? 'nodef'}`}
                                    client={client}
                                    settings={effectiveSettings}
                                    selectedDocumentType={selectedDocumentType}
                                    signaturePreview={signaturePreview}
                                    profilePreview={profilePreview}
                                    direction={direction}
                                    defaultItem={defaultItem}
                                />
                            ) : (
                                <>
                                    {pdfLoading ? (
                                        <Box sx={{
                                            display: 'flex',
                                            height: '100%',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: '8px'
                                        }} dir={direction}>
                                            <Box sx={{ textAlign: 'center' }} dir={direction}>
                                                <CircularProgress sx={{ color: '#1976d2', mb: 1 }} />
                                                <Typography sx={{ color: '#666666', fontSize: '14px' }} dir={direction}>
                                                    {t('GeneratingPreview') || 'Generating preview...'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ) : pdfPreview ? (
                                        <Box sx={{
                                            flex: 1,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'flex-start',
                                            padding: '20px',
                                            backgroundColor: '#f8f9fa'
                                        }} dir={direction}>
                                            <Box sx={{
                                                width: '210mm',
                                                minHeight: '200mm',
                                                backgroundColor: '#ffffff',
                                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                                                borderRadius: '8px',
                                                overflow: 'hidden',
                                                position: 'relative'
                                            }}
                                            >


                                                <iframe
                                                    src={pdfPreview}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        border: 'none',
                                                        minHeight: '293mm',
                                                        pointerEvents: 'none'
                                                    }}
                                                    title="PDF Preview"
                                                />
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Box className={styles.pdfPlaceholder} dir={direction}>
                                            <DescriptionIcon sx={{
                                                fontSize: 48,
                                                color: 'var(--xtram-blue)',
                                                opacity: 0.35,
                                                mb: 2
                                            }} />
                                            <Typography sx={{
                                                color: 'var(--xtram-blue)',
                                                mb: 3,
                                                fontSize: '15px',
                                                textAlign: 'center',
                                                fontFamily: 'var(--xtram-font)'
                                            }} dir={direction}>
                                                {t('PDFPreviewWillAppearHere') || 'PDF preview will appear here'}
                                            </Typography>
                                            <StyledButton
                                                variant="contained"
                                                size="medium"
                                                onClick={() => {
                                                    setPreviewMode('pdf');
                                                    generatePdfPreview();
                                                }}
                                                disabled={pdfLoading}
                                                dir={direction}
                                            >
                                                {pdfLoading ? t('Generating') : t('GeneratePreview')}
                                            </StyledButton>
                                        </Box>
                                    )}
                                </>
                            )}
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions className={styles.dialogActions} dir={direction}>
                    <StyledButton
                        onClick={onClose}
                        variant="outlined"
                        dir={direction}
                        size="medium"
                        sx={{ minWidth: '100px' }}
                    >
                        {t('Cancel')}
                    </StyledButton>
                    <StyledButton
                        onClick={handleSave}
                        variant="contained"
                        startIcon={<Save />}
                        dir={direction}
                        size="medium"
                        disabled={loading}
                        sx={{ minWidth: '120px' }}
                    >
                        {loading ? t('Saving') : t('Save')}
                    </StyledButton>
                </DialogActions>
            </Dialog>

            {/* Create client item modal (from Default item section) - in portal above Dialog so it's focusable */}
            {createItemModalOpen && (
                <Modal open disableAutoFocus disableEnforceFocus sx={{ zIndex: 1400 }}>
                    <ClientItemCreateModal
                        open={createItemModalOpen}
                        onClose={() => setCreateItemModalOpen(false)}
                        onSave={handleSaveNewClientItem}
                        direction={direction}
                        t={t}
                        existingItems={(clientItems || []).map((item) => ({ ...item, makat: item.makat ?? item.Makat }))}
                    />
                </Modal>
            )}

            {/* DraggableModal for PDF Preview */}
            <Dialog
                open={Boolean(newVersionStyleId)}
                onClose={() => setNewVersionStyleId(null)}
                maxWidth="md"
                fullWidth
                sx={{ direction }}
            >
                <DialogTitle dir={direction} sx={{ fontWeight: 700 }}>
                    {tr('ChooseNewVersionLayout', 'בחרו פריסה לגרסה החדשה')}
                </DialogTitle>
                <DialogContent dir={direction}>
                    <DocumentStylePicker
                        t={t}
                        direction={direction}
                        selectedStyleId={newVersionStyleId}
                        onSelect={setNewVersionStyleId}
                        versionCounts={designVersions.reduce((acc, v) => {
                            acc[v.documentStyleVersion] = (acc[v.documentStyleVersion] || 0) + 1;
                            return acc;
                        }, {})}
                    />
                </DialogContent>
                <DialogActions dir={direction} sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button variant="contained" onClick={handleConfirmNewVersionLayout} sx={{ backgroundColor: '#304fff' }}>
                        {tr('Continue', 'המשך')}
                    </Button>
                    <Button variant="outlined" onClick={() => setNewVersionStyleId(null)}>
                        {t('Cancel') || 'ביטול'}
                    </Button>
                </DialogActions>
            </Dialog>
            <DraggableModal
                bigIframeModalOpen={bigIframeModalOpen}
                openedIframeUrl={openedIframeUrl}
                handleCloseIframeModal={handleCloseIframeModal}
            />
        </>
    );
};

export default DocumentSettingsDialog;
